import { HttpErrorResponse } from '@angular/common/http';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import * as fromActions from '../actions/users.management.actions';
import { Role } from '../enums/role.enum';
import { IUser } from '../interfaces/user.interface';
export const usersMngFeatureKey = 'aumicoUsersManagementMicroservice';

export const selectMngFeature = createFeatureSelector<FeatureState>(usersMngFeatureKey);

export interface AppState {
    aumicoUsersManagementMicroservice: FeatureState;
}

interface FeatureState extends EntityState<IUser> {
    error: HttpErrorResponse | null;
    activeManagedUser: string | null;
}

function selectUserId(a: IUser): string {
    return a.id;
}

const adapter: EntityAdapter<IUser> = createEntityAdapter<IUser>({
    selectId: selectUserId,
});

const { selectAll } = adapter.getSelectors();

const getError = (state: FeatureState) => state.error;

export const selectMngError = createSelector(selectMngFeature, getError);

export const selectAllUsers = createSelector(selectMngFeature, selectAll);

const initialState: FeatureState = adapter.getInitialState({
    activeManagedUser: null,
    error: null,
});

const userManagementReducer = createReducer(
    initialState,
    on(fromActions.userManagement.getUsersForAdmin, (state) => ({ ...state, error: null })),
    on(fromActions.userManagement.getUsersForAdminSuccess, (state, action) => {
        const users: IUser[] = action.payload.map((user) => ({
            id: user.id,
            email: user.email,
            organization: user.organizationName,
            state: user.state,
            createdAt: user.createdAt,
            active: `${!user.disabled}`,
            roles: user.roles,
            organizationRole: user.organizationRole,
        }));
        return adapter.setAll(users, { ...state, activeManagedUser: null });
    }),
    on(fromActions.userManagement.getUsersForAdminFailure, (state, action) => ({
        ...state,
        activeManagedUser: null,
        error: action.payload,
    })),

    // Block
    on(fromActions.userManagement.blockUser, (state) => ({ ...state, error: null })),
    on(fromActions.userManagement.blockUserSuccess, (state, action) => {
        const changes = { active: 'false' };
        try {
            return adapter.updateOne({ id: action.payload, changes }, { ...state, error: null });
        } catch (error) {
            return { ...state, error: null };
        }
    }),
    on(fromActions.userManagement.blockUserFailure, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromActions.userManagement.unblockUser, (state) => ({ ...state, error: null })),
    on(fromActions.userManagement.unblockUserSuccess, (state, action) => {
        const changes = { active: 'true' };
        try {
            return adapter.updateOne({ id: action.payload, changes }, { ...state, error: null });
        } catch (error) {
            return { ...state, error: null };
        }
    }),
    on(fromActions.userManagement.unblockUserFailure, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    // demote or promote admin
    on(fromActions.userManagement.demoteUserFromAdmin, (state) => ({ ...state, error: null })),
    on(fromActions.userManagement.demoteUserFromAdminSuccess, (state, action) => {
        const user = { ...state.entities[action.payload] };
        if (!user || Object.keys(user).length === 0) {
            return state;
        }
        const updatedRoles = user.roles.filter((role) => role !== Role.APP_ADMIN);
        const changes = { roles: updatedRoles };
        return adapter.updateOne({ id: action.payload, changes }, { ...state, error: null });
    }),
    on(fromActions.userManagement.demoteUserFromAdminFailure, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromActions.userManagement.promoteUserToAdmin, (state) => ({ ...state, error: null })),
    on(fromActions.userManagement.promoteUserToAdminSuccess, (state, action) => {
        const user = { ...state.entities[action.payload] };
        if (!user || Object.keys(user).length === 0) {
            return state;
        }
        const updatedRoles = [...user.roles, Role.APP_ADMIN];
        const changes = { roles: updatedRoles };
        return adapter.updateOne({ id: action.payload, changes }, { ...state, error: null });
    }),
    on(fromActions.userManagement.promoteUserToAdminFailure, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    // Delete user
    on(fromActions.userManagement.deleteUser, (state) => ({ ...state, error: null })),
    on(fromActions.userManagement.deleteUserSuccess, (state, action) => {
        return adapter.removeOne(action.payload, { ...state, error: null });
    }),
    on(fromActions.userManagement.deleteUserFailure, (state, action) => ({
        ...state,
        error: action.payload,
    })),
);

export function reducer(state: FeatureState | undefined, action: Action) {
    return userManagementReducer(state, action);
}
